import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl, FormattedMessage } from 'react-intl'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import BuyNowBookLaterIcon from 'LuxKit/Icons/custom/BuyNowBookLaterIcon'
import TextButton from 'LuxKit/Button/TextButton'
import SolidPlayIcon from 'LuxKit/Icons/solid/SolidPlayIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import Image from 'components/Common/Image'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import OfferDescription from '../OfferDescription'
import type { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ContactUSP from '../ContactUSP'
import VideoLightbox from 'LuxKit/Carousel/VideoLightbox'
import ModalContext from 'contexts/ModalContext'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Clickable from 'components/Common/Clickable'
import { TextLinkVendor } from 'components/Cruises/CruiseOfferPage/content/CruiseOverview'
import qs from 'qs'
import useCruiseOfferView from 'hooks/Cruise/useCruiseOfferView'
import { usePlaceByName } from 'hooks/usePlace'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import config from 'constants/config'
import TextLink from 'LuxKit/TextLink'
import useCruiseSearchFacets, { FACET_TYPES } from 'hooks/Cruise/useCruiseSearchFacets'
import { LANDING_PAGE_AVAILABLE } from 'constants/cruise'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

interface Props {
  offer: App.TourOffer
  shareConfig: ReturnType<typeof getShareModalConfig>
  showPackageOptions: () => void
}

const OfferTitle = styled(Heading)`
  margin-top: ${rem(10)};
`

const TravelUntilMessageWrapper = styled.div`
  margin-top: ${rem(20)};
`

const StyledOfferDescription = styled(OfferDescription)`
  margin-top: ${rem(20)};
`

const ButtonBar = styled(Group)`
  margin-top: ${rem(20)};
`

const OfferMarkdownRender = styled(MarkdownRender)`
  margin-top: ${rem(16)};
`

const TourLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-inline-start: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const TourInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const OfferBadgeTagWrapper = styled.div`
  margin-top: ${rem(16)};
`

const ContactUSPWrapper = styled.div`
  padding: ${rem(20)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

function FlashTourIntro(props: Props) {
  const {
    offer,
    showPackageOptions,
  } = props

  const intl = useIntl()

  const {
    additionalDescription,
    description,
    inclusions,
    name,
    location,
    vimeoVideoId,
    lowestPricePackage,
    holidayTypes,
    vendorName,
    vendorVehicle,
  } = offer

  const {
    departureCity,
    destinationCity,
    earliestDepartureDate,
    laterDates,
  } = useCruiseOfferView({ offer })
  const departurePortPlace = usePlaceByName(departureCity)
  const destinationPlace = usePlaceByName(destinationCity)

  const showLightbox = useContext(ModalContext)
  const showVideo = () => {
    if (offer.vimeoVideoId) {
      showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)
    }
  }

  const [cruiseFacetsV2] = useCruiseSearchFacets({
    facetTypes: [FACET_TYPES.CRUISE_LINES],
    offerSource: 'cruises',
  })

  const cruiseLine = useMemo(() => {
    return vendorName ? cruiseFacetsV2.cruiseLines.find(({ label }) => vendorName.includes(label)) : undefined
  }, [cruiseFacetsV2.cruiseLines, vendorName])

  const cruiseLineLink = useMemo(() => {
    const queryParams = {
      cruiseLines: cruiseLine?.slug ?? vendorName?.trim(),
    }
    if (queryParams.cruiseLines) {
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [cruiseLine, vendorName])
  const tourLogoAlt = lowestPricePackage.tour.name
    ? intl.formatMessage(
        {
          id: 'ksnGdBOwW3fIxrlVeeVk',
          defaultMessage: '{tourName} logo',
          description: 'Alternative text for a flash tour logo image when the tour name is available',
        },
        { tourName: lowestPricePackage.tour.name },
      )
    : intl.formatMessage({
        id: 'j3VcnxnCVIq0bOCxsjES',
        defaultMessage: 'Tour logo',
        description: 'Alternative text for a flash tour logo image when the tour name is unavailable',
      })

  const vendorVehicleLink = useMemo(() => {
    if (!vendorVehicle || !cruiseLineLink) { return null }

    const vendorVehicleClean = vendorVehicle.replace(/[^\w\s]/gi, '')
    const queryParams = { cruiseShips: vendorVehicleClean.trim() }
    return `${cruiseLineLink}&${qs.stringify(queryParams)}`
  }, [cruiseLineLink, vendorVehicle])

  const getItemLink = useCallback((cruiseLine?: App.CruiseLineFacetV2Item): string => {
    if (!cruiseLine && vendorName) return `/search/cruises?cruiseLines=${vendorName.trim()}`
    if (cruiseLine) {
      if (LANDING_PAGE_AVAILABLE.includes(cruiseLine.slug)) {
        return `/cruise-line/${cruiseLine.label.trim()}`
      }

      return `/search/cruises?cruiseLines=${cruiseLine.slug}`
    }

    return ''
  }, [vendorName])

  const isCruiseOffer = holidayTypes?.includes('Cruises')

  const departurePortLink = useMemo(() => {
    if (departurePortPlace?.name) {
      const queryParams = {
        departureNames: departurePortPlace.name.trim(),
        departureIds: departurePortPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [departurePortPlace])

  const cruisingToLink = useMemo(() => {
    if (departureCity === destinationCity && departurePortPlace?.name) {
      const queryParams = {
        destinationNames: departurePortPlace.name.trim(),
        destinationIds: departurePortPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    if (departureCity !== destinationCity && destinationPlace?.name) {
      const queryParams = {
        destinationNames: destinationPlace.name.trim(),
        destinationIds: destinationPlace.id,
      }
      return `/search/cruises?${qs.stringify(queryParams)}`
    }

    return null
  }, [departureCity, departurePortPlace, destinationCity, destinationPlace])
  const isMobile = useScreenSizeOnly('mobile')
  const nonCruiseLocationLabel = config.LE_TOURS_OFFER_PAGE_VIEW_MAP_BUTTON_ENABLED ? <FormattedMessage
    id="SF5GWnOWKnn0Bu-Eia_U"
    defaultMessage="{location}<mapLink> · view map</mapLink>"
    description="Flash tour location label with view map link on the tour page"
    values={{
      location,
      mapLink: (chunks) => <TextLink href="#getting-there">{chunks}</TextLink>,
    }}
  /> : location

  const departureDateLabelWithMore = <FormattedMessage
    id="ka-nn9OAmPRZWmcihoVw"
    defaultMessage="Departing on {date} <link>and more</link>"
    description="Departure date label with link to view more dates on flash tour intro"
    values={{
      date: earliestDepartureDate,
      link: (chunks) => <TextLink onClick={showPackageOptions}>{chunks}</TextLink>,
    }}
  />
  const departureDateLabel = intl.formatMessage(
    {
      id: 'AsLTDCL6Dz1AYnNXO50L',
      defaultMessage: 'Departing on {date}',
      description: 'Departure date label on flash tour intro on the tour page',
    },
    { date: earliestDepartureDate },
  )

  return (
    <div className="OfferIntro">
      <TourInfo>
        <VerticalSpacer gap={8}>
          {isCruiseOffer && <BodyTextBlock variant="medium" startIcon={isMobile ? null : <LineMapMarkerIcon />} data-testid="offer-location">
            {departureCity === destinationCity && <FormattedMessage
              id="Eg5DBDp-FNmbEvXbqnQl"
              defaultMessage="Starts and ends in <bold>{cityName}</bold>"
              description="Location label showing cruise start and end city on the offer detail page"
              values={{
                cityName: formatPlacePhrase({ location: { name: destinationCity }, language: intl.locale, intent: 'location' }),
                bold: (chunks) => !cruisingToLink ? <b>{chunks}</b> : <TextLinkVendor href={cruisingToLink}><b>{chunks}</b></TextLinkVendor>,
              }}
            />}
            {departureCity !== destinationCity && <FormattedMessage
              id="mr1M9F74QyebETFun_k4"
              defaultMessage="Starts in <startCity>{departureCity}</startCity>. Ends in <endCity>{destinationCity}</endCity>"
              description="Location label showing the cruise departure city and arrival city on the offer page"
              values={{
                departureCity,
                destinationCity,
                startCity: (chunks) => !departurePortLink ? <b>{chunks}</b> : <TextLinkVendor href={departurePortLink}><b>{chunks}</b></TextLinkVendor>,
                endCity: (chunks) => !cruisingToLink ? <b>{chunks}</b> : <TextLinkVendor href={cruisingToLink}><b>{chunks}</b></TextLinkVendor>,
              }}
            />}
          </BodyTextBlock>}
          {!isCruiseOffer && <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />} data-testid="offer-location">
            {nonCruiseLocationLabel}
          </BodyTextBlock>}
          <OfferTitle variant="heading2" forwardedAs="h1">
            {name}
          </OfferTitle>
        </VerticalSpacer>
        {(!cruiseLine && !vendorName) && lowestPricePackage.tour.logoImageId &&
          <TourLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={lowestPricePackage.tour.logoImageId}
            alt={tourLogoAlt}
          />}
        {(cruiseLine || vendorName) && lowestPricePackage.tour.logoImageId && <>
          {isCruiseOffer && <>
            {!cruiseLineLink && <TourLogo
              dpr={2}
              height={75}
              enhancedFormats={false}
              id={lowestPricePackage.tour.logoImageId}
              alt={tourLogoAlt}
            />}
            {!!cruiseLineLink && <Clickable to={cruiseLineLink}>
              <TourLogo
                dpr={2}
                height={75}
                enhancedFormats={false}
                id={lowestPricePackage.tour.logoImageId}
                alt={tourLogoAlt}
              />
            </Clickable>}
          </>}
          {!isCruiseOffer && <Clickable to={getItemLink(cruiseLine)}>
            <TourLogo
              dpr={2}
              height={75}
              enhancedFormats={false}
              id={lowestPricePackage.tour.logoImageId}
              alt={tourLogoAlt}
            />
          </Clickable>}
        </>}
      </TourInfo>

      <VerticalSpacer gap={16}>
        <div>
          {!isCruiseOffer && offer.badge && <OfferBadgeTagWrapper>
            <OfferBadgeLabel badge={offer.badge} />
          </OfferBadgeTagWrapper>}
          {!!earliestDepartureDate && <TravelUntilMessageWrapper>
            <BodyTextBlock
              variant="large"
              weight="bold"
              startIcon={<BuyNowBookLaterIcon />}
            >
              {laterDates.length > 0 ? departureDateLabelWithMore : departureDateLabel}
            </BodyTextBlock>
          </TravelUntilMessageWrapper>}
        </div>
        {isCruiseOffer && <Group direction="vertical" gap={4}>
          <BodyText variant="medium">
            <FormattedMessage
              id="0gqZFvOgHMrNYcwAZm9C"
              defaultMessage="Cruise line: <vendor></vendor>"
              description="Cruise line label with vendor name in the cruise offer or booking section"
              values={{
                vendor: (_chunks) => !cruiseLineLink ? <b>&nbsp;{vendorName}</b> : <TextLinkVendor href={cruiseLineLink}><b>&nbsp;{vendorName}</b></TextLinkVendor>,
              }}
            />
          </BodyText>
          <BodyText variant="medium">
            <FormattedMessage
              id="Pl3k_e8eN52mNUrULD3x"
              defaultMessage="Cruise ship: <ship></ship>"
              description="Cruise ship label with ship name in the cruise offer or booking section"
              values={{
                ship: (_chunks) => !vendorVehicleLink ? <b>&nbsp;{vendorVehicle}</b> : <TextLinkVendor href={vendorVehicleLink}><b>&nbsp;{vendorVehicle}</b></TextLinkVendor>,
              }}
            />
          </BodyText>
        </Group>}
        {!isCruiseOffer && <>
          <BodyText variant="medium">
            <FormattedMessage
              id="0gqZFvOgHMrNYcwAZm9C"
              defaultMessage="Cruise line: <vendor></vendor>"
              description="Cruise line label with vendor name in the cruise offer or booking section"
              values={{
                vendor: (_chunks) => <b>{vendorName}</b>,
              }}
            />
          </BodyText>
          <BodyText variant="medium">
            <FormattedMessage
              id="Pl3k_e8eN52mNUrULD3x"
              defaultMessage="Cruise ship: <ship></ship>"
              description="Cruise ship label with ship name in the cruise offer or booking section"
              values={{
                ship: (_chunks) => <b>{vendorVehicle}</b>,
              }}
            />
          </BodyText>
        </>}

        {isCruiseOffer && !offer.holidayTypes.includes('Ultra Lux') &&
          <ContactUSPWrapper>
            <ContactUSP />
          </ContactUSPWrapper>}
      </VerticalSpacer>
      {description && <StyledOfferDescription description={description} />}
      {inclusions && <OfferMarkdownRender type="compact" fontSize="medium" content={inclusions} />}
      {additionalDescription &&
        <OfferMarkdownRender type="compact" fontSize="medium" content={additionalDescription} />}
      {vimeoVideoId && <ButtonBar direction="horizontal" horizontalAlign="center">
        <TextButton
          kind="secondary"
          startIcon={<SolidPlayIcon />}
          onClick={showVideo}
        >
          <FormattedMessage
            id="t3_ijG6glyDkVmNFc4fp"
            defaultMessage="Watch the video"
            description="Button text to watch tour video on the tour offer detail page"
          />
        </TextButton>
      </ButtonBar>}

      {!isCruiseOffer && <ContactUSP />}
    </div>
  )
}

export default FlashTourIntro
